import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const PredictiveAnalytics = ({ data, recommendations }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-conservation-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Predicted:</span>
              <span className="text-xs font-medium text-accent">{payload?.[0]?.value?.toLocaleString()}</span>
            </div>
            {payload?.[1] && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-muted-foreground">Actual:</span>
                <span className="text-xs font-medium text-primary">{payload?.[1]?.value?.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg p-6 shadow-conservation border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">Predictive Analytics</h3>
          <p className="text-sm text-muted-foreground">Peak season planning and resource optimization</p>
        </div>
        <Icon name="TrendingUp" size={24} className="text-primary" />
      </div>
      <div className="w-full h-80 mb-6" aria-label="Predictive Analytics Area Chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="colorPredicted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ffc107" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#ffc107" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#02453e" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#02453e" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f3a39" />
            <XAxis 
              dataKey="period" 
              stroke="#b0bec5"
              tick={{ fill: '#b0bec5', fontSize: 12 }}
            />
            <YAxis 
              stroke="#b0bec5"
              tick={{ fill: '#b0bec5', fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="predicted" 
              stroke="#ffc107" 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorPredicted)"
              name="Predicted Visitors"
            />
            <Area 
              type="monotone" 
              dataKey="actual" 
              stroke="#02453e" 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorActual)"
              name="Actual Visitors"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Lightbulb" size={20} className="text-accent" />
          <h4 className="text-sm font-semibold text-foreground">Resource Allocation Recommendations</h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recommendations?.map((rec, index) => (
            <div key={index} className="bg-muted/30 rounded-lg p-4 border-l-4" style={{ borderColor: rec?.priority === 'high' ? '#f44336' : rec?.priority === 'medium' ? '#ffc107' : '#1b8a5b' }}>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  rec?.priority === 'high' ? 'bg-error/20' : 
                  rec?.priority === 'medium'? 'bg-warning/20' : 'bg-success/20'
                }`}>
                  <Icon 
                    name={rec?.icon} 
                    size={18} 
                    className={
                      rec?.priority === 'high' ? 'text-error' : 
                      rec?.priority === 'medium'? 'text-warning' : 'text-success'
                    }
                  />
                </div>
                <div className="flex-1">
                  <h5 className="text-sm font-medium text-foreground mb-1">{rec?.title}</h5>
                  <p className="text-xs text-muted-foreground mb-2">{rec?.description}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      rec?.priority === 'high' ? 'bg-error/20 text-error' : 
                      rec?.priority === 'medium'? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
                    }`}>
                      {rec?.priority?.toUpperCase()}
                    </span>
                    <span className="text-xs text-muted-foreground">{rec?.timeframe}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalytics;